// Internal modules
import type { ConfigType } from './config';
import { config } from './config';
import { logger } from '../logger';
import { isTest } from '../utils/env';
import { getOverrideParams, getParams } from '../utils/params';
import {
  getCallbackIntegrationType,
  getCallbackVersion,
  getCheckoutSessionFlags,
  getGlobals,
  getLocale,
  getToken,
  getUnifiedLoginContextData,
} from '../globals';
import { isFunction } from '@/lib/utils/typeUtils';
import {
  getCountryConfigValue,
  getFactors,
  getFptiFlowName,
  getTreatment,
  hasExactTreatment,
  hasTreatment,
  includesTreatment,
} from './utils';
import {
  AML_COUNTRIES,
  BOLT_COUNTRIES,
  DCC_INELIGIBLE_COUNTRIES,
  DEFAULT_ENABLED_LANGUAGES,
  REGULATION_E_COUNTRIES,
  STREAMLINE_COUNTRIES_WITH_CHECKBOX_TERMS,
} from '../constants';
import { TREATMENTS } from './constants';
import { VENMO_OVERLAY_VARIANTS } from '../../components/VenmoMessage/constants';
import {
  getAccountBasedForcedSignupEligibility,
  getForcedGuestEligibility,
  getMemberMatchNoOptInEligibility,
  getPasswordlessUpgradeEligibility,
  getPOMAEligibility,
  getPPReactMockStylesEligibility,
  getStreamlineForcedEligibility,
  getStreamlineOptionalEligibility,
  getWpsGuestUpgradeEligibility,
  passwordlessForcedSignupEligibility,
  passwordlessOptionalSignupEligibility,
} from './featureEligibility';
import type { GenericObjectType, NullableCheckoutSession } from '@/types';
import { BUTTON_COLOR_TREATMENT, REBRAND_BUTTON_TREATMENT } from '@/types';
import { CountryCodes } from '@/generated/graphql';
import type { BillingType } from '@/generated/graphql';
import type { ThreeDsV1Data } from '@/store/models/api/types';
import { FEATURE_SIGNUP_RIGHT_OF_WITHDRAW_CHECKBOX_COUNTRIES } from '../globals/constants';

export const resolvers = {
  allowChangePhoneType: (): boolean =>
    hasTreatment('FEATURE_ALLOW_CHANGE_PHONE_TYPE'),

  allowedBankTypes: (): string[] | null | undefined => {
    const { checkoutSession } = getGlobals().initialGqlData;
    const { cardProductClasses } = checkoutSession || {};
    // @ts-expect-error - Not altering functional code in file rename
    // TODO: GQL says this should be DEBIT_CARD
    const isDebitFlow = cardProductClasses?.includes('DEBIT') || false;

    // This logic is currently only used for MX
    // return featured bank list only if we are sure that it's not a debit flow
    if (!cardProductClasses || isDebitFlow) {
      return [];
    }

    return getCountryConfigValue('allowedBankTypes', config);
  },

  applePayButtonShown: (): boolean =>
    hasTreatment('FEATURE_GUEST_APPLE_PAY_SHOWN'),

  applePayNewSupportedNetworks: (): boolean =>
    hasTreatment('FEATURE_GUEST_APPLE_PAY_NEW_NETWORKS'),

  // TODO: What's the purpose of this? Should it really be in features?
  billingType: (): BillingType | null | undefined =>
    getGlobals().initialGqlData.checkoutSession?.cart?.billingType,

  blockDomesticPayers: (): boolean => {
    const merchantCountry =
      getGlobals().initialGqlData?.checkoutSession?.merchant?.country;
    const { country: buyerCountry } = getLocale();

    return (
      BOLT_COUNTRIES.includes(buyerCountry) && buyerCountry === merchantCountry
    );
  },
  blockNonDomesticCards: (): boolean =>
    Boolean(getCheckoutSessionFlags().blockNonDomesticCards),

  blockNonDomesticPayer: (): boolean =>
    Boolean(getCheckoutSessionFlags().blockNonDomesticPayer),

  blockNonDomesticShipping: (): boolean =>
    Boolean(getCheckoutSessionFlags().blockNonDomesticShipping),

  bnplExperience: (): '' | 'TAB' | 'LINK' => {
    const { isBillingAgreement, isDonation, isWPS } = getCheckoutSessionFlags();
    const isBnplIneligible = isBillingAgreement || isDonation || isWPS;

    const { checkoutSession = {} as NullableCheckoutSession } =
      getGlobals().initialGqlData;
    const { upstreamCreditPresentment } = checkoutSession?.creditMeta || {};
    const creditOffers =
      checkoutSession?.nonMemberCreditOffers?.allOffers || [];
    const creditEligibility = creditOffers.reduce<GenericObjectType>(
      (accum, offer) => {
        // @ts-expect-error - Not altering functional code in file rename
        // TODO: GQL says offer and creditProductIdentifier can be nullable
        accum[offer.creditProductIdentifier] = offer.isEligible;
        return accum;
      },
      {},
    );

    const isBnplEligible =
      !isBnplIneligible && (upstreamCreditPresentment || creditEligibility.GPL);

    logger.fpti('bnpl_eligiblity_check', {
      bnpl_eligible: isBnplEligible ? 'Y' : 'N',
      event_name: 'bnpl_guest_eligibility_event',
      ppc_eligible: creditEligibility.PPC ? 'Y' : 'N',
      state_name: 'bnpl_guest_eligibility',
      transition_name: 'bnpl_eligibility_check',
      upstream_presentment: upstreamCreditPresentment ? 'Y' : 'N',
    });

    if (!isBnplEligible) {
      return '';
    }

    if (upstreamCreditPresentment) {
      if (getTreatment('FEATURE_BNPL_EXPERIENCE_WITH_UPSTREAM')) {
        const isTabBNPLExperience = hasTreatment(
          'FEATURE_BNPL_EXPERIENCE_WITH_UPSTREAM',
        );

        return isTabBNPLExperience ? 'TAB' : 'LINK';
      }
    } else if (creditEligibility.GPL) {
      const isLinkBNPLExperience = hasExactTreatment({
        feature: 'FEATURE_BNPL_EXPERIENCE',
        treatment: 'Trmt_FEATURE_BNPL_LINK',
      });
      const isTabBNPLExperience = hasExactTreatment({
        feature: 'FEATURE_BNPL_EXPERIENCE',
        treatment: 'Trmt_FEATURE_BNPL_TAB',
      });

      if (isLinkBNPLExperience) {
        return 'LINK';
      }

      if (isTabBNPLExperience) {
        return 'TAB';
      }
    }

    return '';
  },

  /**
   * @returns An enum value that will be used to choose the button color throughout the application.
   */
  buttonColor: (): BUTTON_COLOR_TREATMENT => {
    if (
      hasExactTreatment({
        feature: 'FEATURE_CTA_BUTTON_COLOR',
        treatment: BUTTON_COLOR_TREATMENT.Treatment1,
      })
    ) {
      return BUTTON_COLOR_TREATMENT.Treatment1;
    }
    if (
      hasExactTreatment({
        feature: 'FEATURE_CTA_BUTTON_COLOR',
        treatment: BUTTON_COLOR_TREATMENT.Treatment2,
      })
    ) {
      return BUTTON_COLOR_TREATMENT.Treatment2;
    }
    if (
      hasExactTreatment({
        feature: 'FEATURE_CTA_BUTTON_COLOR',
        treatment: BUTTON_COLOR_TREATMENT.Treatment3,
      })
    ) {
      return BUTTON_COLOR_TREATMENT.Treatment3;
    }

    return BUTTON_COLOR_TREATMENT.Control;
  },

  canDialingCodeChange: (): boolean =>
    hasTreatment('FEATURE_ALLOW_CHANGE_DIALING_CODE'),

  CTAMessageTreatment: (): string => {
    const experiment = 'FEATURE_CPA_DISCLAIMER_MESSAGING';

    const treatment = getTreatment(experiment)?.startsWith('Trmt')
      ? 'TREATMENT_' + getTreatment(experiment)?.split('_')[5]
      : 'CONTROL';

    return treatment;
  },

  enabledLanguages: (): string[] => {
    const { localeMetadata } = getGlobals().initialGqlData;
    const { supportedLanguages } = localeMetadata || {};

    return supportedLanguages || DEFAULT_ENABLED_LANGUAGES;
  },

  footerLinks: (): ConfigType['footerLinks'] => config.footerLinks,

  // This feature flag is used only in dev/staging environment to run functional tests
  // This flag has no purpose in production/sandbox environment
  forcePasskeyEnrollment: (): boolean => false,

  hasAddBank: (): boolean => hasTreatment('FEATURE_HAS_ADD_BANK'),

  hasAddressAutocomplete: (): boolean => {
    // @TO_DO @TECH_DEBT we disable GAAC because we use ANS by zip code in BR
    // we should remove this one once we ramp up GAAC in BR
    if (getLocale().country === 'BR') {
      return false;
    }

    // Adding OR condition to make sure there are no side-effects to existing
    // allocations and validate new allocation to test GAAC with Enhancements
    // specifically for mobile in US, CA and GB
    return (
      hasTreatment('FEATURE_ADDRESS_AUTOCOMPLETE') ||
      hasTreatment('ADDRESSQUALITY_AUTOCOMPLETE_SINGLE_LINE')
    );
  },

  hasAddressAutocompleteDisclaimer: (): boolean => {
    return hasExactTreatment({
      feature: 'FEATURE_ADDRESS_AUTOCOMPLETE',
      treatment: 'Trmt_FEATURE_ADDRESS_AUTOCOMPLETE_WITH_DISCLAIMER',
    });
  },

  hasAddressAutoCompleteFiveItems: (): boolean =>
    includesTreatment({
      feature: 'ADDRESSQUALITY_AUTOCOMPLETE_SINGLE_LINE',
      treatment: 'Trmt_ADDRESSQUALITY_AUTOCOMPLETE_FIVE_ITEMS',
    }),

  hasAddressAutocompletePrivacyLink: (): boolean => {
    return hasExactTreatment({
      feature: 'GOOGLE_AUTOCOMPLETE_PRIVACY_LINK',
      treatment: 'Trmt_GOOGLE_AUTOCOMPLETE_PRIVACY_LINK',
    });
  },

  hasAddressAutoCompleteSingleLine: (): boolean =>
    includesTreatment({
      feature: 'ADDRESSQUALITY_AUTOCOMPLETE_SINGLE_LINE',
      treatment: 'Trmt_ADDRESSQUALITY_AUTOCOMPLETE_SINGLE_LINE',
    }),

  hasAddressAutocompleteWithNoBrowserAutofill: (): boolean => {
    return hasExactTreatment({
      feature: 'FEATURE_ADDRESS_AUTOCOMPLETE',
      treatment: 'Trmt_FEATURE_ADDRESS_AUTOCOMPLETE_SUPPRESS_BROWSER_AUTOFILL',
    });
  },

  hasAddressNormalizationBoth: (): boolean =>
    includesTreatment({
      feature: 'FEATURE_HAS_ADDRESS_NORMALIZATION',
      treatment: 'Trmt_FEATURE_HAS_ADDRESS_NORMALIZATION_2',
    }),

  hasAddressNormalizationShipping: (): boolean =>
    includesTreatment({
      feature: 'FEATURE_HAS_ADDRESS_NORMALIZATION',
      treatment: 'Trmt_FEATURE_HAS_ADDRESS_NORMALIZATION_1',
    }),

  hasCardTypeSelector: (): boolean =>
    hasTreatment('FEATURE_HAS_CARD_TYPE_SELECTOR'),

  hasConsumerAddBank: (): boolean =>
    hasTreatment('FEATURE_HAS_CONSUMER_ADD_BANK'),

  hasDccDomesticRestriction: (): boolean =>
    hasTreatment('FEATURE_HAS_DCC_DOMESTIC_RESTRICTION'),

  hasDccIofDisclosure: (): boolean =>
    hasTreatment('FEATURE_HAS_DCC_IOF_DISCLOSURE'),

  hasFooterEntityMessage: (): boolean =>
    hasTreatment('FEATURE_HAS_FOOTER_ENTITY_MESSAGE'),

  hasFooterPrivacyAndSecurityMessage: (): boolean =>
    hasTreatment('FEATURE_HAS_FOOTER_PRIVACY_AND_SECURITY_MSG'),

  hasGuestTermsAndConditionsCheckbox: (): boolean => {
    const isStreamlineExperienceEligible = getStreamlineOptionalEligibility();

    return (
      hasTreatment('FEATURE_HAS_GUEST_TERMS_CHECKBOX') &&
      !isStreamlineExperienceEligible
    );
  },

  hasGuestXOSepaUpdate: (): boolean => {
    const fundingSource =
      window?.xprops?.fundingSource || getParams().fundingSource;
    const isFundingSourceSepa = fundingSource === 'sepa';
    const hasGuestXOSepaUpdate =
      isFundingSourceSepa && CountryCodes.De === getLocale().country;

    return hasGuestXOSepaUpdate;
  },

  hasMarketingOptInCheckbox: (): boolean =>
    hasTreatment('FEATURE_HAS_MARKETING_OPT_IN_CHECKBOX'),

  hasPrefilledShippingAddress: (): boolean => {
    // Address fields to consider to determine whether merchant sending any
    // pre-fill address or not.
    const addressFields = ['line1', 'city', 'state', 'postalCode'];
    const { shippingAddress = {} } =
      getGlobals()?.initialGqlData?.checkoutSession?.cart || {};
    const isPrefillAddress = addressFields.some(addressField =>
      // @ts-expect-error
      // TODO: Correct the index - not adjusting functional code in part 1
      Boolean(shippingAddress[addressField]),
    );

    return isPrefillAddress;
  },

  hasPrefilledShippingOptions: (): boolean => {
    const { shippingMethods } =
      getGlobals()?.initialGqlData?.checkoutSession?.cart || {};

    return Boolean(shippingMethods?.length);
  },

  hasPrimaryResidentialAddress: (): boolean =>
    hasTreatment('FEATURE_RESIDENTIAL_ADDRESS_LITE'),

  hasPrivacyStatementTerms: (): boolean =>
    hasTreatment('FEATURE_PRIVACY_STATEMENTS'),

  hasRebrandAlertTreatment: (): boolean =>
    hasExactTreatment({
      feature: 'FEATURE_REBRAND_ALERTS',
      treatment: 'Trmt_FEATURE_REBRAND_ALERTS',
    }),

  hasRebrandColorTreatment: (): boolean =>
    hasExactTreatment({
      feature: 'FEATURE_REBRAND_INTERACTIVE_ELEMENTS',
      treatment: 'Trmt_FEATURE_REBRAND_INTERACTIVE_ELEMENTS',
    }),

  hasRebrandFontTreatment: (): boolean =>
    hasExactTreatment({
      feature: 'FEATURE_REBRAND_FONT',
      treatment: 'Trmt_FEATURE_REBRAND_FONT_1',
    }),

  hasShippingAddressQualityExperiment: (): boolean =>
    includesTreatment({
      feature: 'ADDRESSQUALITY_AUTOCOMPLETE_SINGLE_LINE',
      treatment: 'Trmt_ADDRESSQUALITY_AUTOCOMPLETE_SINGLE_LINE',
    }) ||
    includesTreatment({
      feature: 'ADDRESSQUALITY_AUTOCOMPLETE_SINGLE_LINE',
      treatment: 'Trmt_ADDRESSQUALITY_AUTOCOMPLETE_FIVE_ITEMS',
    }),

  hasSignupTermsAndConditionsCheckbox: (): boolean => {
    const isStreamlineExperienceEligible =
      getStreamlineForcedEligibility() || getStreamlineOptionalEligibility();
    /*
      This check enables validation of our signupAgreeToTerms store
      field for 2 streamline countries, which require T&C checkbox.
      When we are in streamline experience
    */
    const isStreamlineTermsWithCheckbox = Boolean(
      STREAMLINE_COUNTRIES_WITH_CHECKBOX_TERMS.includes(getLocale()?.country) &&
        isStreamlineExperienceEligible,
    );

    return (
      (hasTreatment('FEATURE_HAS_SIGNUP_TERMS_CHECKBOX') &&
        !isStreamlineExperienceEligible) ||
      isStreamlineTermsWithCheckbox
    );
  },

  /** Feature flag for whether to display the third party data consent checkbox in KR */
  hasThirdPartyDataConsentCheckbox: (): boolean =>
    hasTreatment('FEATURE_THIRD_PARTY_DATA_CONSENT'),

  /** Feature flag for whether to display the unique information processing checkbox in KR.
   *
   * Currently connected to the same ELMO as hasThirdPartyDataConsentCheckbox but split into its
   * own feature flag in case a future country is required to add this checkbox.
   */
  hasUniqueInformationProcessingCheckbox: (): boolean =>
    hasTreatment('FEATURE_THIRD_PARTY_DATA_CONSENT'),

  hideShipping: (): boolean => Boolean(getCheckoutSessionFlags().hideShipping),

  isAddressless: (): boolean => {
    const merchantCountry =
      getGlobals().initialGqlData?.checkoutSession?.merchant?.country;
    const {
      hideShipping,
      isChangeShippingAddressAllowed,
      isGuestEligible,
      isShippingAddressRequired,
    } = getCheckoutSessionFlags();
    const { country: userCountry } = getLocale();
    const isAddresslessEligible =
      !isShippingAddressRequired && hasTreatment('FEATURE_IS_ADDRESSLESS');
    const isCrossBorder = merchantCountry !== userCountry;

    // Temporary log to capture data around number of eligible address less txn
    logger.fpti('IS_ADDRESS_LESS_ELIGIBLE', {
      country: userCountry,
      hide_shipping: hideShipping ? 1 : 0,
      is_guest_eligible: isGuestEligible ? 1 : 0,
      is_shipping_chng_allowed: isChangeShippingAddressAllowed ? 1 : 0,
      iscbr: isCrossBorder ? 1 : 0,
      noshipping: isShippingAddressRequired ? 0 : 1,
      state_name: 'billing',
      transition_name: 'process_address_less_eligibility',
    });
    return isAddresslessEligible && !isCrossBorder;
  },

  isAllowChangeShipping: (): boolean =>
    Boolean(getCheckoutSessionFlags().isChangeShippingAddressAllowed),

  isAmlCountry: (): boolean => AML_COUNTRIES.includes(getLocale().country),

  isBillingAgreement: (): boolean =>
    getCheckoutSessionFlags()?.isBillingAgreement ?? false,

  isCurrencyConversionInfoMsgStyleActive: (): boolean =>
    hasTreatment('FEATURE_CURRENCY_CONVERSION_INFO_MSG_STYLE'),

  isDccEligible: (): boolean =>
    !DCC_INELIGIBLE_COUNTRIES.includes(getLocale().country),

  /** Checks checkout session flags and elmo treatment to see if debit card is required */
  isDebitRequiredTransaction: (): boolean => {
    const { isDebitRequired } = getCheckoutSessionFlags();

    const hasRequireDebitTreatment = hasExactTreatment({
      feature: 'FEATURE_RMG_REQUIRE_DEBIT_GUEST',
      treatment: 'Trmt_FEATURE_RMG_REQUIRE_DEBIT_GUEST_1',
    });

    return Boolean(isDebitRequired && hasRequireDebitTreatment);
  },

  isDonation: (): boolean => Boolean(getCheckoutSessionFlags().isDonation),

  isExpandedPurchaseProtection: (): boolean =>
    hasExactTreatment({
      feature: 'FEATURE_EXPAND_VALUE_PROPS',
      treatment: 'Trmt_FEATURE_EXPAND_VALUE_PROPS_PURCHASE_PROTECTION',
    }),

  // This is not a stretch reward, although the treatment name appears as such.
  isExpandedRewards: (): boolean =>
    hasExactTreatment({
      feature: 'FEATURE_EXPAND_VALUE_PROPS',
      treatment: 'Trmt_FEATURE_EXPAND_VALUE_PROPS_STRETCH_REWARDS_V2',
    }),

  isExpandedStretchRewards: (): boolean => {
    const { isSignupIncentiveEligibleStretchRewards } =
      getCheckoutSessionFlags();
    const stretchTreatment = hasExactTreatment({
      feature: 'FEATURE_EXPAND_VALUE_PROPS',
      treatment: 'Trmt_FEATURE_EXPAND_VALUE_PROPS_STRETCH_REWARDS',
    });
    return Boolean(isSignupIncentiveEligibleStretchRewards && stretchTreatment);
  },

  isExpandedStretchRewardsIneligibleDownstream: (): boolean => {
    const { isSignupIncentiveEligibleStretchRewards } =
      getCheckoutSessionFlags();
    const stretchTreatment = hasExactTreatment({
      feature: 'FEATURE_EXPAND_VALUE_PROPS',
      treatment: 'Trmt_FEATURE_EXPAND_VALUE_PROPS_STRETCH_REWARDS',
    });

    return Boolean(
      !isSignupIncentiveEligibleStretchRewards && stretchTreatment,
    );
  },

  isExpandedValueProps: (): boolean =>
    hasTreatment('FEATURE_EXPAND_VALUE_PROPS'),

  isExpandMemberMatchWPS: (): boolean =>
    hasTreatment('FEATURE_EXPAND_MEMBER_MATCHING_WPS'),

  isForcedGuest: (): boolean => {
    return (
      getForcedGuestEligibility() ||
      (getParams().fallbackFrom === 'axo' &&
        Boolean(getCheckoutSessionFlags().isGuestEligible))
    );
  },

  isForcedSignUp: (): boolean => {
    const isRPSForcedSignup = includesTreatment({
      feature: 'FEATURE_MERCHANT_BASED_FLOW_TYPE',
      treatment: 'FORCED_SIGNUP',
    });
    const isAccountBasedForcedSignup = getAccountBasedForcedSignupEligibility();

    return (
      isRPSForcedSignup ||
      isAccountBasedForcedSignup ||
      !getCheckoutSessionFlags().isGuestEligible
    );
  },

  isInstallmentsEligible: (): boolean => {
    const intent =
      getGlobals().initialGqlData.checkoutSession?.cart?.intent || '';
    // Installments are eligible only for intent: SALE,
    // other intents like CAPTURE/AUTHORIZE/ORDER not eligible.
    return (
      intent === 'SALE' &&
      Boolean(getCountryConfigValue('isInstallmentsEligible', config))
    );
  },

  isInvoiceFlow: (): boolean =>
    Boolean(
      getGlobals()?.initialGqlData?.checkoutSession?.flags?.isInvoiceFlow,
    ),

  isMarketingOptInCheckedByDefault: (): boolean =>
    hasTreatment('FEATURE_IS_MKT_OPT_IN_CHECKED_BY_DEFAULT'),

  isMemberMatchEligibleReqAndOptSignup: (): boolean =>
    hasTreatment('FEATURE_EXPAND_MEMBER_MATCHING'),

  isMemberMatchNoOptIn: (): boolean => getMemberMatchNoOptInEligibility(),

  isMemberMatchNoToggleWithInterstitial: (): boolean => {
    const ecNoToggleInterstitial = includesTreatment({
      feature: 'FEATURE_EXPAND_MEMBER_MATCHING',
      treatment: 'FEATURE_EXPAND_MEMBER_MATCHING_NO_TOGGLE__WITH_INTERSTITIAL',
    });

    const wpsNoToggleInterstitial = includesTreatment({
      feature: 'FEATURE_EXPAND_MEMBER_MATCHING_WPS',
      treatment:
        'FEATURE_EXPAND_MEMBER_MATCHING_WPS_NO_TOGGLE__WITH_INTERSTITIAL',
    });

    return ecNoToggleInterstitial || wpsNoToggleInterstitial;
  },

  // Optional guest, not forced into guest or signup
  isOptionalSignup: (): boolean => {
    const isGuestEligible = Boolean(getCheckoutSessionFlags().isGuestEligible);
    // Forced signup with or without password
    const isForcedSignup = includesTreatment({
      feature: 'FEATURE_MERCHANT_BASED_FLOW_TYPE',
      treatment: 'FORCED_SIGNUP',
    });
    const isAccountBasedForcedSignup = getAccountBasedForcedSignupEligibility();

    return isGuestEligible && !isForcedSignup && !isAccountBasedForcedSignup;
  },

  isPasswordlessUpgrade: (): boolean => getPasswordlessUpgradeEligibility(),

  isPayerLoginCountryNotAllowed: (): boolean =>
    hasExactTreatment({
      feature: 'FEATURE_LOGIN_REGION_RESOLUTION_WEASLEY',
      treatment: 'Trmt_FEATURE_LOGIN_REGION_RESOLUTION_WEASLEY',
    }),

  isPaymentServiceActApplied: (): boolean => {
    return getLocale().country === 'SG';
  },

  isPOMAEligible: (): boolean => getPOMAEligibility(),

  isPPReactStylesEligible: (): boolean => getPPReactMockStylesEligibility(),

  isRegeEligible: (): boolean => {
    const { country: userCountry } = getLocale();
    const isInvoiceFlow = Boolean(
      getGlobals()?.initialGqlData?.checkoutSession?.flags?.isInvoiceFlow,
    );
    const merchantCountry =
      getGlobals()?.initialGqlData?.checkoutSession?.merchant?.country;
    return (
      REGULATION_E_COUNTRIES.includes(userCountry) &&
      merchantCountry !== userCountry &&
      isInvoiceFlow
    );
  },

  isServerSideShippingCallbacksEligible: (): boolean => {
    const isShippingChangeAllowed = Boolean(
      getCheckoutSessionFlags().isChangeShippingAddressAllowed,
    );
    const hideShipping = Boolean(getCheckoutSessionFlags().hideShipping);

    return (
      getCallbackIntegrationType() === 'SERVER_SIDE' &&
      getCallbackVersion() === 'V2' &&
      isShippingChangeAllowed &&
      !hideShipping
    );
  },

  isSharedFIEligible: (): boolean => {
    const { isGuestEligible, isWPS } = getCheckoutSessionFlags();
    const integrationType = isWPS ? 'WPS' : 'EC';
    // Forced signup with or without password
    const isForcedSignup = includesTreatment({
      feature: 'FEATURE_MERCHANT_BASED_FLOW_TYPE',
      treatment: 'FORCED_SIGNUP',
    });
    const isAccountBasedForcedSignup = getAccountBasedForcedSignupEligibility();

    logger.fpti('billing_signup_shift_eligibility_check', {
      component: 'checkoutuinodeweb_weasley',
      context_id: getToken(),
      context_type: `${integrationType}-token`,
      experience: isAccountBasedForcedSignup
        ? 'shift_eligible'
        : 'shift_uneligible',
      feed_name: 'checkoutuinodeweb',
      is_guest_eligible: !!isGuestEligible,
      product: 'billing',
      state_name: 'billing',
      transition_name: 'shift_eligibility_check',
    });

    return (
      Boolean(isGuestEligible) && !isForcedSignup && !isAccountBasedForcedSignup
    );
  },

  isShippingAndTaxId: (): boolean =>
    hasTreatment('FEATURE_GUEST_SHIPPING_AND_TAX_ID'),

  /**
   * The shipping feature in the smart button(SPB) integration
   * has two versions of callbacks:
   *  1. `onShippingChange` is the current callback or V1.
   *  2. `onShippingOptionsChange` and `onShippingAddressChange` are
   *    the newer callbacks(V2)
   *
   * Summary:
   * The merchants that are integrated with the current
   * `onShippingChange` callback have to handle calculating taxes, updating
   * shipping options, and patching the order. This is a lot of overhead on the
   * merchant side that can open doors for integration errors. Hence, the SDK
   * team implemented new callbacks to move some of this overhead to be
   * handled on the SDK side. This should simplify the shipping feature
   * integration on the merchant side.
   *
   * For more info, please see {@link https://engineering.paypalcorp.com/confluence/pages/viewpage.action?pageId=853380692}
   */
  isShippingCallbacksV2Eligible: (): boolean => {
    const isShippingChangeAllowed = Boolean(
      getCheckoutSessionFlags().isChangeShippingAddressAllowed,
    );

    logger.info('IS_SHIPPING_CHANGE_ALLOWED', { isShippingChangeAllowed });
    logger.info('HAS_ON_SHIPPING_ADDRESS_CHANGE', {
      hasOnShippingAddressChange: isFunction(
        window?.xprops?.onShippingAddressChange,
      ),
    });

    logger.info('HAS_ON_SHIPPING_OPTIONS_CHANGE', {
      hasOnShippingOptionsChange: isFunction(
        window?.xprops?.onShippingOptionsChange,
      ),
    });

    return (
      isFunction(window?.xprops?.onShippingAddressChange) &&
      isFunction(window?.xprops?.onShippingOptionsChange) &&
      isShippingChangeAllowed
    );
  },

  isShippingCallbackV1Eligible: (): boolean => {
    const isShippingChangeAllowed = Boolean(
      getCheckoutSessionFlags().isChangeShippingAddressAllowed,
    );

    logger.info('HAS_ON_SHIPPING_CHANGE', {
      hasOnShippingChange: isFunction(window?.xprops?.onShippingChange),
    });
    return (
      isFunction(window?.xprops?.onShippingChange) &&
      hasTreatment('XO_SPB_SHIPPING_CALLBACK') &&
      isShippingChangeAllowed
    );
  },

  isShippingCallbackV1Ineligible: (): boolean =>
    isFunction(window?.xprops?.onShippingChange) &&
    !hasTreatment('XO_SPB_SHIPPING_CALLBACK'),

  isSignupIncentiveEligible: (): boolean =>
    Boolean(
      getGlobals().initialGqlData?.checkoutSession?.flags
        ?.isSignupIncentiveEligible,
    ),

  isSntSupported: (): boolean =>
    Boolean(getCheckoutSessionFlags().isSntSupported),

  isStreamlineForcedExperience: (): boolean => getStreamlineForcedEligibility(),

  isStreamlineOptionalExperience: (): boolean =>
    getStreamlineOptionalEligibility(),

  isWebLLS: (): boolean => hasTreatment('FEATURE_ONBOARDING_WEBLLS'),

  isWps: (): boolean =>
    Boolean(
      getCheckoutSessionFlags().isWPS || getGlobals().landingIWCData?.isWPS,
    ),

  isWpsGuestUpgradeEligible: (): boolean => getWpsGuestUpgradeEligibility(),

  // TODO: Due to dynamic nature of KYC the resolved types are difficult
  // Need to refactor the code or spend more time on solving typings or kyx
  kycFields: (): any => getCountryConfigValue('kycFields', config),

  // TODO: Due to dynamic nature of KYC the resolved types are difficult
  // Need to refactor the code or spend more time on solving typings or kyx
  kycIdentityDocumentNumberLabelKey: (): any =>
    getCountryConfigValue('kycIdentityDocumentNumberLabelKey', config),

  // TODO: Due to dynamic nature of KYC the resolved types are difficult
  // Need to refactor the code or spend more time on solving typings or kyx
  kycIdTypes: (): any => getCountryConfigValue('kycIdTypes', config),

  // TODO: Due to dynamic nature of KYC the resolved types are difficult
  // Need to refactor the code or spend more time on solving typings or kyx
  kycUsesNationalityOnChange: (): any =>
    getCountryConfigValue('kycUsesNationalityOnChange', config),

  passwordlessForcedSignup: (): boolean =>
    passwordlessForcedSignupEligibility(),

  passwordlessOptionalSignup: (): boolean =>
    passwordlessOptionalSignupEligibility(),

  /**
   * Determines which treatment to use for the PayPal rebrand CTA button
   * experiment.
   */
  primaryCtaButtonColor: (): REBRAND_BUTTON_TREATMENT => {
    if (
      hasExactTreatment({
        feature: 'FEATURE_PAYSHEET_CTA_BUTTON_COLOR',
        treatment: REBRAND_BUTTON_TREATMENT.Treatment1,
      })
    ) {
      return REBRAND_BUTTON_TREATMENT.Treatment1;
    }
    if (
      hasExactTreatment({
        feature: 'FEATURE_PAYSHEET_CTA_BUTTON_COLOR',
        treatment: REBRAND_BUTTON_TREATMENT.Treatment2,
      })
    ) {
      return REBRAND_BUTTON_TREATMENT.Treatment2;
    }
    if (
      hasExactTreatment({
        feature: 'FEATURE_PAYSHEET_CTA_BUTTON_COLOR',
        treatment: REBRAND_BUTTON_TREATMENT.Treatment3,
      })
    ) {
      return REBRAND_BUTTON_TREATMENT.Treatment3;
    }

    return REBRAND_BUTTON_TREATMENT.Control;
  },

  rebrandFormFieldsTheme: (): string =>
    hasExactTreatment({
      feature: 'FEATURE_REBRAND_FORM_FIELDS',
      treatment: 'Trmt_FEATURE_REBRAND_FORM_FIELDS',
    })
      ? 'v8'
      : '',

  // TODO: Due to dynamic nature of KYC the resolved types are difficult
  // Need to refactor the code or spend more time on solving typings or kyx
  secondaryKycIdTypes: (): any =>
    getCountryConfigValue('secondaryKycIdTypes', config),

  // TODO: Due to dynamic nature of KYC the resolved types are difficult
  // Need to refactor the code or spend more time on solving typings or kyx
  selectAllSignupFieldsKeys: (): any =>
    getCountryConfigValue('selectAllSignupFields', config),

  shouldBlockDomesticTransactions: (): boolean =>
    hasTreatment('FEATURE_SHOULD_BLOCK_DOMESTIC_TRANSACTION'),

  shouldCollapseNationalitySelectBox: (): boolean =>
    hasTreatment('FEATURE_NATIONALITY_COLLAPSE'),

  shouldCollectCardProductClass: (): boolean => {
    // https://admin.paypalinc.com/elmo/experiment/102768
    const { country } = getLocale();
    return country === CountryCodes.Br;
  },

  shouldDetectCupCardType: (): boolean =>
    hasTreatment('FEATURE_SHOULD_DETECT_CUP_CARDTYPE'),

  shouldDisableOnboardToggleForMembers: (): boolean =>
    includesTreatment({
      feature: 'FEATURE_OPTIONAL_SIGNUP_UI_EXP',
      treatment: 'HIDE_SIGNUP_FOR_MEMBERS',
    }),

  /**
   * Once the Gibraltar experiment is fully ramped,
   * we should remove the `shouldDisableSignupForGibraltar`
   * feature flag and reset `shouldDisableSignup` to
   * use `hasTreatment` instead of `hasExactTreatment`.
   */
  shouldDisableSignup: (): boolean =>
    hasExactTreatment({
      feature: 'FEATURE_SHOULD_DISABLE_SIGNUP',
      treatment: 'Trmt_FEATURE_SHOULD_DISABLE_SIGNUP_1',
    }),

  /**
   * This is TEMPORARY feature flag that will be used to
   * disable signup for gibraltar. It needed to be separated from
   * the original flag `shouldDisableSignup` because
   * we need to know whether countries OTHER THAN GIBRALTAR should
   * remove the option to select Gibraltar from the country dropdown list
   * while concurrently disabling signup for Gibraltar.
   * Since OTHER countries need to know this information, we could not
   * put "GI" as a filter in ELMO because then
   * other countries would be excluded from the experiment altogether.
   */
  shouldDisableSignupForGibraltar: (): boolean =>
    hasExactTreatment({
      feature: 'FEATURE_SHOULD_DISABLE_SIGNUP',
      treatment: 'Trmt_FEATURE_SHOULD_DISABLE_SIGNUP_AND_FILTER_COUNTRY_LIST',
    }),

  shouldDisableSignupForIndia: (): boolean => {
    const { country } = getLocale();
    return (
      hasExactTreatment({
        feature: 'FEATURE_SHOULD_DISABLE_SIGNUP',
        treatment: 'Trmt_FEATURE_SHOULD_DISABLE_SIGNUP_1',
      }) && country === CountryCodes.In
    );
  },

  shouldDisplayJpKanaAndKanjiNames: (): boolean =>
    hasTreatment('FEATURE_JP_KANA_NAMES'),

  shouldShowCRSInterstitial: (): boolean =>
    hasTreatment(TREATMENTS.FEATURE_CRS_DATA_COLLECTION),

  shouldShowDomesticUserAgreementAcceptance: (): boolean => {
    const { country: buyerCountry } = getLocale();
    const merchantCountry =
      getGlobals()?.initialGqlData?.checkoutSession?.merchant?.country;
    const isDomesticTransaction = buyerCountry === merchantCountry;

    return (
      isDomesticTransaction &&
      hasTreatment('FEATURE_GUEST_UA_ACCEPTANCE_WEASLEY')
    );
  },

  shouldShowILPSLArticle2Content: (): boolean =>
    hasTreatment('FEATURE_IL_PSL_ARTICLE_2'),

  shouldShowInstallmentCostToBuyer: (): boolean =>
    hasTreatment('FEATURE_INSTALLMENT_COST_TO_BUYER'),

  shouldShowLegalAgeDisclosure: (): boolean =>
    hasTreatment('FEATURE_LEGAL_AGE_DISCLOSURE'),

  shouldShowMobileNumberHintLang: (): boolean => {
    const PWDlessOptionalSignup = hasExactTreatment({
      feature: 'FEATURE_OPTIONAL_SIGNUP_UI_EXP',
      treatment:
        'Trmt_FEATURE_OPTIONAL_SIGNUP_STREAMLINE_WITHOUT_PASSWORD_HIDE_SIGNUP_FOR_MEMBERS',
    });
    const PWDlessRequiredSignup = hasExactTreatment({
      feature: 'FEATURE_FORCED_SIGNUP_UI_EXP',
      treatment: 'Trmt_FEATURE_FORCED_SIGNUP_STREAMLINE_WITHOUT_PASSWORD',
    });
    const isMobileHintEligible = hasExactTreatment({
      feature: 'FEATURE_PWDLESS_MOBILE_HINT_LANG',
      treatment: 'Trmt_FEATURE_PWDLESS_MOBILE_HINT_LANG',
    });

    return Boolean(
      isMobileHintEligible && (PWDlessOptionalSignup || PWDlessRequiredSignup),
    );
  },

  /* temporary ramp, to be removed in first half of 2024 */
  shouldShowNewPhilippinesFooterDisclaimer: (): boolean =>
    hasTreatment('FEATURE_PH_FOOTER_3PL_LICENSE_WEASLEY'),

  shouldShowOptionalUpsellHalfSheet: (): boolean => {
    const { country } = getLocale();
    const { device } = getGlobals();
    const channel = device?.channel;
    const isMobile = channel === 'MOBILE';
    const isGuestEligible =
      getGlobals()?.initialGqlData?.checkoutSession?.flags?.isGuestEligible;
    return isMobile && !!isGuestEligible && country === 'US';
  },

  shouldShowOvercaptureDisclaimerInCommit: (): boolean =>
    hasTreatment('FEATURE_OVERCAPTURE_PAY_NOW_WEASLEY'),

  shouldShowRebrandedErrors: (): boolean =>
    hasTreatment('FEATURE_REBRANDED_ERRORS_GUEST_SIGNUP'),

  shouldShowSCAPhoneConfirmation: (): boolean =>
    hasTreatment(TREATMENTS.FEATURE_SIGNUP_MANDATORY_PHONE_CONFIRMATION),

  shouldShowSignupIncentiveDelayedControl: (): boolean =>
    !hasTreatment('FEATURE_SIGNUP_INCENTIVES_DELAYED'),

  shouldShowSignupIncentiveDelayedVariant1: (): boolean =>
    includesTreatment({
      feature: 'FEATURE_SIGNUP_INCENTIVES_DELAYED',
      treatment: 'SIGNUP_INCENTIVES_DELAYED_VARIANT_1',
    }),

  shouldShowSignupIncentiveDelayedVariant2: (): boolean =>
    includesTreatment({
      feature: 'FEATURE_SIGNUP_INCENTIVES_DELAYED',
      treatment: 'SIGNUP_INCENTIVES_DELAYED_VARIANT_2',
    }),

  shouldShowSignupIncentiveDelayedVariant3: (): boolean =>
    includesTreatment({
      feature: 'FEATURE_SIGNUP_INCENTIVES_DELAYED',
      treatment: 'SIGNUP_INCENTIVES_DELAYED_VARIANT_3',
    }),

  shouldShowSplitLineOne: (): boolean => {
    // @TO_DO @TECH_DEBT we disable GAAC because we use ANS by zip code in BR
    // we should remove this one once we ramp up GAAC in BR
    return getLocale().country === 'BR';
  },

  // TODO: Fast follow-up. This should be an Elmo (FEATURE_SUPPRESS_SMS_OTP_LOGIN).
  //       Elmo stage: https://msmaster.qa.paypal.com/laelmo/experiments/156611/settings
  /**
   * As part of regulatory MFA track, we need to suppress SMS OTP based logins
   * for existing PayPal accounts as those logins are not MFA compliant.
   */
  shouldSuppressSMSOTPLogin: (): boolean => true,

  /**
   * Whether or not we should use colors matching pp-react v8.
   */
  shouldUsePayPalRebrandStyles: (): boolean =>
    hasExactTreatment({
      feature: 'FEATURE_PP_REBRAND',
      treatment: 'Trmt_FEATURE_PP_REBRAND_V1',
    }),

  signupRightOfWithdrawalCheckbox: (): boolean =>
    FEATURE_SIGNUP_RIGHT_OF_WITHDRAW_CHECKBOX_COUNTRIES.includes(
      getLocale().country,
    ),
  signupRightOfWithdrawalInUserAgreement: (): boolean =>
    includesTreatment({
      feature: 'FEATURE_SIGNUP_RIGHT_OF_WITHDRAW',
      treatment: 'Trmt_FEATURE_SIGNUP_RIGHT_OF_WITHDRAW_1',
    }),

  // TODO: After global utils are typed correct this type
  threeDsV1Experience: (): ThreeDsV1Data['experience'] =>
    getFactors('FEATURE_3DS_V1_EXPERIENCE')?.experience,

  venmoOverlay: (): string => {
    if (!hasTreatment('FEATURE_VENMO_OVERLAY_VARIANT')) {
      return VENMO_OVERLAY_VARIANTS.CONTROL;
    }

    const treatment = getTreatment('FEATURE_VENMO_OVERLAY_VARIANT') || '';

    if (!Object.values(VENMO_OVERLAY_VARIANTS).includes(treatment)) {
      return VENMO_OVERLAY_VARIANTS.CONTROL;
    }

    return treatment;
  },
};
// TODO: This should likely be readonly, but currently many tests are
// manually setting values. Will circle back on this in team discussion

export type ResolverType = typeof resolvers;
export type ResolverKey = keyof ResolverType;
export type ResolverConfig = {
  [Property in keyof ResolverType]: ReturnType<ResolverType[Property]>;
};

export const resolveFeatures = (): ResolverConfig => {
  try {
    const clientFeatures = Object.entries(resolvers).reduce(
      (accum, [featureName, resolver]) => ({
        ...accum,
        [featureName]: resolver(),
      }),
      {} as ResolverConfig,
    );
    const { email, hasExistingAccount } = getUnifiedLoginContextData();
    const allowURLOverrides =
      getGlobals()?.isInternalRequest || getGlobals()?.deployEnv?.sandbox;

    logger.info('all_client_features_resolved', clientFeatures);
    logger.fpti('render_ui', {
      email_type: hasExistingAccount ? 'existing' : 'new',
      flow_name: getFptiFlowName({ clientFeatures }),
      is_email_present: email ? 'Y' : 'n',
      state_name: 'prepare_billing_page_render',
      transition_name: 'process_billing_page_render',
    });

    return {
      ...clientFeatures,
      ...(allowURLOverrides && getOverrideParams({ paramType: 'featureFlag' })),
    };
  } catch (err: any) {
    // If we hit this error then the page render is mostly likely to have
    // a problem in rendering the complete app successfully
    logger.error('resolve_features_error', {
      error: err.message,
      stack: err.stack,
    });
    // TODO: this can be removed at later point after VIP page ramp
    logger.flush();
    return {} as ResolverConfig;
  }
};

// Access individual resolvers only in test env
if (isTest) {
  resolveFeatures.resolvers = resolvers;
}
